/**
 * Pagos Page — Monthly payment tracker (mobile-first card list)
 * src/pages/Pagos.jsx
 */

import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, addDoc } from 'firebase/firestore';
import { ChevronLeft, ChevronRight, ChevronRight as ArrowRight, Wallet } from 'lucide-react';
import { showToast } from '../components/Toast';
import { logActivity } from '../utils/auditLog';
import { useSettings } from '../context/SettingsContext';

const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const MONTHS_SHORT = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(n || 0);

const now = new Date();
const TODAY_MONTH_KEY = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

const monthKeyToLabel = (key) => {
  if (!key) return '';
  const [y, m] = key.split('-');
  return `${MONTHS_ES[parseInt(m, 10) - 1]} ${y}`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
};

const MONEDA_OPTIONS = ['MXN', 'USD', 'EUR'];
const CATEGORIAS = ['Alimentación','Educación','Salud','Hogar','Transporte','Entretenimiento','Ropa','Tecnología','Inversión','Ahorro','Préstamo','Servicio','Otro'];

export default function Pagos({ db, user }) {
  const { t, rates } = useSettings();
  const [gastos,  setGastos]  = useState([]);
  const [pasivos, setPasivos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedMonthKey, setSelectedMonthKey] = useState(TODAY_MONTH_KEY);
  const [availableMonths,  setAvailableMonths]  = useState([TODAY_MONTH_KEY]);
  const [allChecklist,     setAllChecklist]     = useState({});
  const [allPagos,         setAllPagos]         = useState([]);

  const [personFilter, setPersonFilter] = useState('Todos');
  const [rowModal,     setRowModal]     = useState(null);
  const [pasivoModal,  setPasivoModal]  = useState(false);
  const [selPasivo,    setSelPasivo]    = useState('');
  const [pagoAmount,   setPagoAmount]   = useState('');
  const [pagoSaving,   setPagoSaving]   = useState(false);
  const [extraModal,   setExtraModal]   = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [gasSnap, pasSnap, checkSnap, pagosSnap] = await Promise.all([
          getDocs(collection(db, 'gastos')),
          getDocs(collection(db, 'pasivos')),
          getDocs(collection(db, 'checklist')),
          getDocs(collection(db, 'pagos')),
        ]);
        setGastos(gasSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPasivos(pasSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        const clMap = {};
        checkSnap.docs.forEach(d => { clMap[d.id] = d.data(); });
        setAllChecklist(clMap);
        setAllPagos(pagosSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        const clMonths   = checkSnap.docs.map(d => d.id).filter(id => /^\d{4}-\d{2}$/.test(id));
        const pagoMonths = pagosSnap.docs.map(d => d.data().month).filter(Boolean);
        const merged     = [...new Set([...clMonths, ...pagoMonths, TODAY_MONTH_KEY])].sort();
        setAvailableMonths(merged);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [db]);

  const checks           = allChecklist[selectedMonthKey] || {};
  const currentPagosList = useMemo(() => allPagos.filter(p => p.month === selectedMonthKey), [allPagos, selectedMonthKey]);

  const monthIdx   = availableMonths.indexOf(selectedMonthKey);
  const canPrev    = monthIdx > 0;
  const canNext    = monthIdx < availableMonths.length - 1;
  const monthLabel = monthKeyToLabel(selectedMonthKey);

  const ensureMonthAvailable = (key) =>
    setAvailableMonths(prev => prev.includes(key) ? prev : [...prev, key].sort());

  const getCheck = (id, gasto) => {
    const v = checks[id];
    if (!v || typeof v === 'boolean') return { pagado: !!v, pago: gasto?.monto || 0, paidBy: null, paidAt: null, editedBy: null, editedAt: null };
    return {
      pagado:    !!v.pagado,
      pago:      v.pago ?? (gasto?.monto || 0),
      paidBy:    v.paidBy   || null,
      paidAt:    v.paidAt   || null,
      editedBy:  v.editedBy || null,
      editedAt:  v.editedAt || null,
    };
  };

  const userName = user?.displayName || user?.email?.split('@')[0] || 'Usuario';

  const handleSaveRow = async () => {
    if (!rowModal) return;
    setRowModal(r => ({ ...r, saving: true }));
    try {
      const { row, pagado, pago, payee } = rowModal;
      const wasPreviouslyPaid = row.pagado;
      const tsNow = new Date().toISOString();

      const checkEntry = { pagado, pago: parseFloat(pago) || 0 };
      if (payee !== undefined) checkEntry.payee = payee;

      if (pagado) {
        if (wasPreviouslyPaid) {
          checkEntry.paidBy  = row.paidBy  || userName;
          checkEntry.paidAt  = row.paidAt  || tsNow;
          checkEntry.editedBy = userName;
          checkEntry.editedAt = tsNow;
        } else {
          checkEntry.paidBy = userName;
          checkEntry.paidAt = tsNow;
        }
      }

      const newMonthData = { ...(allChecklist[selectedMonthKey] || {}), [row.id]: checkEntry };
      await setDoc(doc(db, 'checklist', selectedMonthKey), newMonthData);
      setAllChecklist(prev => ({ ...prev, [selectedMonthKey]: newMonthData }));
      ensureMonthAvailable(selectedMonthKey);

      const action = wasPreviouslyPaid ? 'editar' : 'crear';
      logActivity(db, user, action, 'Pago', `${row.descripcion} — ${fmt(parseFloat(pago) || 0)}${pagado ? ' \u2713' : ''}`);

      setRowModal(null);
      showToast(wasPreviouslyPaid ? 'Pago actualizado' : 'Pago guardado');
    } catch (err) {
      console.error(err);
      showToast('Error al guardar', 'error');
      setRowModal(r => ({ ...r, saving: false }));
    }
  };

  const handlePagoPasivo = async () => {
    if (!selPasivo || !pagoAmount) return;
    setPagoSaving(true);
    try {
      const pasivo  = pasivos.find(p => p.id === selPasivo);
      const paid    = parseFloat(pagoAmount) || 0;
      const newMonto = Math.max(0, (pasivo.monto || 0) - paid);
      await updateDoc(doc(db, 'pasivos', selPasivo), { monto: newMonto });
      const tsNow = new Date().toISOString();
      const newPago = {
        type: 'pasivo', pasivoId: selPasivo, pasivoTipo: pasivo.tipo,
        person: pasivo.person, month: selectedMonthKey, monto: paid,
        paidBy: userName, paidAt: tsNow, timestamp: tsNow,
      };
      const ref = await addDoc(collection(db, 'pagos'), newPago);
      setAllPagos(prev => [...prev, { id: ref.id, ...newPago }]);
      setPasivos(prev => prev.map(p => p.id === selPasivo ? { ...p, monto: newMonto } : p));
      ensureMonthAvailable(selectedMonthKey);
      logActivity(db, user, 'crear', 'Pago Pasivo', `${pasivo.tipo} — ${fmt(paid)}`);
      setPasivoModal(false); setSelPasivo(''); setPagoAmount('');
      showToast('Pago de pasivo registrado');
    } catch (err) { console.error(err); showToast('Error al registrar', 'error'); }
    finally { setPagoSaving(false); }
  };

  const handlePagoExtra = async () => {
    if (!extraModal || !extraModal.categoria || !extraModal.monto) return;
    setExtraModal(m => ({ ...m, saving: true }));
    try {
      const tsNow = new Date().toISOString();
      const newPago = {
        type:      'extra',
        person:    extraModal.person,
        categoria: extraModal.categoria,
        esencial:  extraModal.esencial,
        payee:     extraModal.payee || '',
        monto:     parseFloat(extraModal.monto) || 0,
        moneda:    extraModal.moneda || 'MXN',
        month:     selectedMonthKey,
        paidBy:    userName,
        paidAt:    tsNow,
        timestamp: tsNow,
      };
      const ref = await addDoc(collection(db, 'pagos'), newPago);
      setAllPagos(prev => [...prev, { id: ref.id, ...newPago }]);
      ensureMonthAvailable(selectedMonthKey);
      logActivity(db, user, 'crear', 'Pago Extra', `${extraModal.categoria}${extraModal.payee ? ' \u00b7 ' + extraModal.payee : ''} — ${fmt(parseFloat(extraModal.monto))}`);
      setExtraModal(null);
      showToast('Pago extra registrado');
    } catch (err) {
      console.error(err);
      showToast('Error al registrar', 'error');
      setExtraModal(m => ({ ...m, saving: false }));
    }
  };

  // Totals
  const totalGastos       = gastos.reduce((s, g) => s + (g.monto || 0), 0);
  const gastosPagadoTotal = gastos.filter(g => getCheck(g.id, g).pagado).reduce((s, g) => s + (getCheck(g.id, g).pago || 0), 0);
  const extraPaidTotal    = currentPagosList.filter(p => p.type === 'extra').reduce((s, p) => s + (p.monto || 0), 0);
  const pasivosPaidTotal  = currentPagosList.filter(p => p.type === 'pasivo').reduce((s, p) => s + (p.monto || 0), 0);
  const totalPagado       = gastosPagadoTotal + extraPaidTotal + pasivosPaidTotal;
  const totalPorPagar     = totalGastos - gastosPagadoTotal;

  // Merged rows
  const mergedRows = useMemo(() => {
    const gastoRows = gastos
      .filter(g => personFilter === 'Todos' || g.person === personFilter)
      .map(g => {
        const c = getCheck(g.id, g);
        return {
          _type: 'gasto', id: g.id, person: g.person,
          descripcion: g.categoria, presupuesto: g.monto || 0,
          pagado: c.pagado, pago: c.pago,
          paidBy: c.paidBy, paidAt: c.paidAt,
          editedBy: c.editedBy, editedAt: c.editedAt,
          payee: c.payee || g.payee || '',
          moneda: g.moneda || 'MXN',
          _raw: g,
        };
      })
      .sort((a, b) => {
        if (a.pagado !== b.pagado) return a.pagado ? 1 : -1;
        return String(a.descripcion).localeCompare(String(b.descripcion), 'es');
      });

    const pagoRows = currentPagosList
      .filter(p => p.type === 'pasivo')
      .filter(p => personFilter === 'Todos' || p.person === personFilter)
      .map(p => ({
        _type: 'pago_pasivo', id: p.id, person: p.person,
        descripcion: p.pasivoTipo || 'Pasivo', presupuesto: 0,
        pagado: true, pago: p.monto || 0,
        paidBy: p.paidBy || null, paidAt: p.paidAt || null,
        editedBy: null, editedAt: null,
        payee: '', moneda: 'MXN', _raw: p,
      }));

    const extraRows = currentPagosList
      .filter(p => p.type === 'extra')
      .filter(p => personFilter === 'Todos' || p.person === personFilter)
      .map(p => ({
        _type: 'extra', id: p.id, person: p.person,
        descripcion: p.categoria, presupuesto: 0,
        pagado: true, pago: p.monto || 0,
        paidBy: p.paidBy || null, paidAt: p.paidAt || null,
        editedBy: null, editedAt: null,
        payee: p.payee || '', moneda: p.moneda || 'MXN', _raw: p,
      }));

    return [...gastoRows, ...pagoRows, ...extraRows];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gastos, currentPagosList, personFilter, checks]);

  const selPasivoData = pasivos.find(p => p.id === selPasivo);
  const personColor   = (p) => p === 'Tumi' ? '#8b5cf6' : '#3b82f6';

  if (loading) return (
    <div className="page-container">
      <div className="app-loading" style={{ minHeight: '40vh' }}><div className="spinner" /></div>
    </div>
  );

  const inputStyle = { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', fontSize: '0.9rem', background: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.375rem' };

  return (
    <div className="page-container">
      <h1 className="page-title">{t('pagos')} <span style={{fontSize:'0.7rem', background:'rgba(16,185,129,0.12)', color:'#10b981', borderRadius:'0.35rem', padding:'0.2rem 0.5rem', fontWeight:700, verticalAlign:'middle', marginLeft:'0.3rem'}}>MXN</span></h1>

      {/* Summary */}
      <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', marginBottom: '1rem' }}>
        <div className="summary-card">
          <div className="summary-card-label">Total {t('expenses')}</div>
          <div className="summary-card-value" style={{ color: '#ef4444' }}>{fmt(totalGastos)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">{t('paid')}</div>
          <div className="summary-card-value" style={{ color: '#10b981' }}>{fmt(totalPagado)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">{t('pending')}</div>
          <div className="summary-card-value" style={{ color: '#f59e0b' }}>{fmt(totalPorPagar)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Pasivos</div>
          <div className="summary-card-value" style={{ color: '#ef4444' }}>{fmt(pasivosPaidTotal)}</div>
        </div>
      </div>

      {/* Month navigator + filters */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <button onClick={() => setSelectedMonthKey(availableMonths[monthIdx - 1])} disabled={!canPrev}
            style={{ background: 'none', border: 'none', cursor: canPrev ? 'pointer' : 'not-allowed', color: canPrev ? 'var(--text-primary)' : 'var(--text-light)', padding: '0.25rem', display: 'flex', alignItems: 'center' }}>
            <ChevronLeft size={22} />
          </button>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', minWidth: '9rem', textAlign: 'center' }}>{monthLabel}</span>
          <button onClick={() => setSelectedMonthKey(availableMonths[monthIdx + 1])} disabled={!canNext}
            style={{ background: 'none', border: 'none', cursor: canNext ? 'pointer' : 'not-allowed', color: canNext ? 'var(--text-primary)' : 'var(--text-light)', padding: '0.25rem', display: 'flex', alignItems: 'center' }}>
            <ChevronRight size={22} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {[t('all'), 'Tumi', 'Alberto'].map(f => (
            <button key={f} className={`filter-btn${personFilter === f ? ' active' : ''}`}
              onClick={() => setPersonFilter(f === t('all') ? 'Todos' : f)}>
              {f}
            </button>
          ))}
          <button onClick={() => setExtraModal({ person: 'Tumi', categoria: '', esencial: 'Si', payee: '', monto: '', moneda: 'MXN', saving: false })}
            style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.75rem', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <Wallet size={14} /> Pago Extra
          </button>
          <button onClick={() => setPasivoModal(true)}
            style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.75rem', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
            Pago Pasivos
          </button>
        </div>
      </div>

      {/* Card list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {mergedRows.length === 0 && (
          <div className="card" style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Sin registros para {monthLabel}
          </div>
        )}

        {mergedRows.map(row => {
          const isPasivo = row._type === 'pago_pasivo';
          const isExtra  = row._type === 'extra';
          const tappable = !isPasivo;
          const paidFooter = row.pagado && row.paidBy;
          const footerText = row.editedBy
            ? `${t('editedBy')} ${row.editedBy} \u00b7 ${fmtDate(row.editedAt)}`
            : row.paidBy ? `${t('paidBy')} ${row.paidBy} \u00b7 ${fmtDate(row.paidAt)}` : null;

          return (
            <div
              key={`${row._type}-${row.id}`}
              onClick={() => {
                if (!tappable) return;
                setRowModal({ row, pagado: row.pagado, pago: row.pago, payee: row.payee, saving: false });
              }}
              style={{
                display: 'flex', flexDirection: 'column',
                background: row.pagado ? 'rgba(16,185,129,0.07)' : 'var(--bg-secondary)',
                borderRadius: '0.75rem',
                border: `1px solid ${row.pagado ? 'rgba(16,185,129,0.2)' : 'var(--border-color)'}`,
                padding: '0.75rem 1rem',
                cursor: tappable ? 'pointer' : 'default',
                transition: 'background 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {/* Person color bar */}
                <div style={{ width: 4, height: 40, borderRadius: 2, background: personColor(row.person), flexShrink: 0 }} />

                {/* Center info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                    {isPasivo && <span style={{ fontSize: '0.68rem', background: 'rgba(239,68,68,0.12)', color: '#ef4444', borderRadius: '0.25rem', padding: '0.1rem 0.35rem', fontWeight: 700 }}>PASIVO</span>}
                    {isExtra && <span style={{ fontSize: '0.68rem', background: 'rgba(59,130,246,0.12)', color: '#3b82f6', borderRadius: '0.25rem', padding: '0.1rem 0.35rem', fontWeight: 700 }}>EXTRA</span>}
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: personColor(row.person) }}>{row.person}</span>
                    {(row.moneda && row.moneda !== 'MXN') && (
                      <span style={{ fontSize: '0.68rem', background: 'rgba(245,158,11,0.12)', color: '#f59e0b', borderRadius: '0.25rem', padding: '0.1rem 0.35rem', fontWeight: 700 }}>{row.moneda}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.92rem', fontWeight: 600, color: row.pagado ? 'var(--text-light)' : 'var(--text-primary)', textDecoration: (!isPasivo && !isExtra && row.pagado) ? 'line-through' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.descripcion}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {!isPasivo && !isExtra && <span>Pres: {fmt(row.presupuesto)}</span>}
                    {row.payee && <span style={{ color: 'var(--text-light)' }}>{'\u00b7'} {row.payee}</span>}
                  </div>
                </div>

                {/* Right: amount + status */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, color: row.pagado ? '#10b981' : (isPasivo ? '#ef4444' : 'var(--text-primary)') }}>
                    {fmt(row.pago)}
                  </div>
                  <div style={{ fontSize: '0.72rem', marginTop: '0.15rem' }}>
                    {row.pagado
                      ? <span style={{ color: '#10b981', fontWeight: 600 }}>{'\u2713'} {t('paid')}</span>
                      : <span style={{ color: '#f59e0b', fontWeight: 600 }}>{t('pending')}</span>
                    }
                  </div>
                </div>

                {tappable && <ArrowRight size={16} style={{ color: 'var(--text-light)', flexShrink: 0 }} />}
              </div>

              {/* Paid footer */}
              {paidFooter && footerText && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-light)', marginTop: '0.4rem', paddingTop: '0.4rem', borderTop: '1px solid var(--border-color)', paddingLeft: '1rem' }}>
                  {footerText}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Row edit modal */}
      {rowModal && (() => {
        const gasto = rowModal.row._raw;
        const moneda = gasto?.moneda || 'MXN';
        const rate = moneda === 'USD' ? (rates?.USD || 20) : moneda === 'EUR' ? (rates?.EUR || 22) : 1;
        const mxnEquivalent = moneda !== 'MXN' ? (parseFloat(rowModal.pago) || 0) * rate : null;
        return (
          <div className="modal-overlay" onClick={() => setRowModal(null)}>
            <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 400, width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
                <div style={{ width: 6, height: 28, borderRadius: 3, background: personColor(rowModal.row.person), flexShrink: 0 }} />
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)', flex: 1 }}>{rowModal.row.descripcion}</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{t('person')}</span>
                  <span style={{ fontWeight: 700, color: personColor(rowModal.row.person) }}>{rowModal.row.person}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{t('budget')}</span>
                  <span style={{ fontWeight: 600 }}>{fmt(rowModal.row.presupuesto)}</span>
                </div>
                {moneda !== 'MXN' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{t('currency')}</span>
                    <span style={{ fontWeight: 600, color: '#f59e0b' }}>{moneda} {'\u00b7'} 1 {moneda} = {moneda === 'USD' ? rates.USD : rates.EUR} MXN</span>
                  </div>
                )}
              </div>

              {/* Payee field */}
              <div style={{ marginBottom: '0.875rem' }}>
                <label style={labelStyle}>{t('payee')}</label>
                <input type="text" value={rowModal.payee || ''} onChange={e => setRowModal(r => ({ ...r, payee: e.target.value }))} placeholder="Ej. Walmart, Dr. Garcia..." style={inputStyle} />
              </div>

              {/* Monto input */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>
                  {moneda !== 'MXN' ? `Monto (${moneda})` : 'Monto Pagado (MXN)'}
                </label>
                <input type="number" inputMode="decimal" value={rowModal.pago}
                  onChange={e => setRowModal(r => ({ ...r, pago: e.target.value }))}
                  style={{ ...inputStyle, textAlign: 'right' }} />
                {mxnEquivalent !== null && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', textAlign: 'right', marginTop: '0.25rem' }}>
                    {'\u2248'} {fmt(mxnEquivalent)} MXN
                  </div>
                )}
              </div>

              {/* Pagado toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', padding: '0.75rem 1rem', background: 'var(--bg-tertiary)', borderRadius: '0.5rem' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t('markAsPaid')}</span>
                <button onClick={() => setRowModal(r => ({ ...r, pagado: !r.pagado }))}
                  style={{ width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', background: rowModal.pagado ? '#10b981' : 'var(--border-color)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                  <span style={{ position: 'absolute', top: 3, left: rowModal.pagado ? 25 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', display: 'block' }} />
                </button>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={() => setRowModal(null)}
                  style={{ flex: 1, padding: '0.7rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
                  {t('cancel')}
                </button>
                <button onClick={handleSaveRow} disabled={rowModal.saving}
                  style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: '0.5rem', background: '#1a472a', color: 'white', cursor: rowModal.saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.9rem', opacity: rowModal.saving ? 0.7 : 1 }}>
                  {rowModal.saving ? t('loading') : t('save')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pago Pasivos modal */}
      {pasivoModal && (
        <div className="modal-overlay" onClick={() => setPasivoModal(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 400, width: '100%' }}>
            <h3 style={{ margin: '0 0 1.25rem', color: '#ef4444', fontSize: '1.1rem' }}>Registrar Pago de Pasivo</h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Pasivo</label>
              <select value={selPasivo} onChange={e => { setSelPasivo(e.target.value); setPagoAmount(pasivos.find(p => p.id === e.target.value)?.pagoMensual || ''); }}
                style={inputStyle}>
                <option value="">— Selecciona —</option>
                {pasivos.map(p => <option key={p.id} value={p.id}>{p.person} {'\u00b7'} {p.tipo} — {fmt(p.monto)}</option>)}
              </select>
            </div>
            {selPasivoData && (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Saldo actual</span>
                  <span style={{ fontWeight: 700, color: '#ef4444' }}>{fmt(selPasivoData.monto)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Pago mensual</span>
                  <span style={{ fontWeight: 700 }}>{fmt(selPasivoData.pagoMensual || 0)}</span>
                </div>
              </div>
            )}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Monto Pagado (MXN)</label>
              <input type="number" inputMode="decimal" value={pagoAmount} onChange={e => setPagoAmount(e.target.value)} placeholder="0.00"
                style={{ ...inputStyle, textAlign: 'right' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => { setPasivoModal(false); setSelPasivo(''); setPagoAmount(''); }}
                style={{ flex: 1, padding: '0.7rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                {t('cancel')}
              </button>
              <button onClick={handlePagoPasivo} disabled={!selPasivo || !pagoAmount || pagoSaving}
                style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: '0.5rem', background: '#1a472a', color: 'white', cursor: 'pointer', fontWeight: 600, opacity: (!selPasivo || !pagoAmount || pagoSaving) ? 0.6 : 1 }}>
                {pagoSaving ? t('loading') : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pago Extra modal */}
      {extraModal && (
        <div className="modal-overlay" onClick={() => setExtraModal(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, width: '100%' }}>
            <h3 style={{ margin: '0 0 1.25rem', color: '#3b82f6', fontSize: '1.1rem' }}>{t('extraPaymentTitle')}</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.25rem' }}>
              {/* Person */}
              <div>
                <label style={labelStyle}>{t('person')}</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['Tumi', 'Alberto'].map(p => (
                    <button key={p} onClick={() => setExtraModal(m => ({ ...m, person: p }))}
                      style={{ flex: 1, padding: '0.5rem', border: `2px solid ${extraModal.person === p ? personColor(p) : 'var(--border-color)'}`, borderRadius: '0.5rem', background: extraModal.person === p ? `${personColor(p)}15` : 'var(--bg-tertiary)', color: personColor(p), fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Categoria */}
              <div>
                <label style={labelStyle}>{t('category')}</label>
                <select value={extraModal.categoria} onChange={e => setExtraModal(m => ({ ...m, categoria: e.target.value }))} style={inputStyle}>
                  <option value="">— Selecciona —</option>
                  {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Esencial */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.875rem', background: 'var(--bg-tertiary)', borderRadius: '0.5rem' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t('essential')}</span>
                <button onClick={() => setExtraModal(m => ({ ...m, esencial: m.esencial === 'Si' ? 'No' : 'Si' }))}
                  style={{ width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', background: extraModal.esencial === 'Si' ? '#10b981' : 'var(--border-color)', position: 'relative', transition: 'background 0.2s' }}>
                  <span style={{ position: 'absolute', top: 3, left: extraModal.esencial === 'Si' ? 25 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', display: 'block' }} />
                </button>
              </div>

              {/* Payee */}
              <div>
                <label style={labelStyle}>{t('payee')}</label>
                <input type="text" value={extraModal.payee} onChange={e => setExtraModal(m => ({ ...m, payee: e.target.value }))} placeholder="Ej. Walmart, Dr. Garcia, Amazon..." style={inputStyle} />
              </div>

              {/* Moneda + Monto */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{ flex: '0 0 90px' }}>
                  <label style={labelStyle}>{t('currency')}</label>
                  <select value={extraModal.moneda} onChange={e => setExtraModal(m => ({ ...m, moneda: e.target.value }))} style={inputStyle}>
                    {MONEDA_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>{t('amount')}</label>
                  <input type="number" inputMode="decimal" value={extraModal.monto} onChange={e => setExtraModal(m => ({ ...m, monto: e.target.value }))} placeholder="0.00" style={{ ...inputStyle, textAlign: 'right' }} />
                </div>
              </div>

              {/* Exchange rate hint */}
              {extraModal.moneda !== 'MXN' && extraModal.monto && (
                <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '0.5rem', padding: '0.6rem 0.875rem' }}>
                  {'\u2248'} {fmt((parseFloat(extraModal.monto) || 0) * (extraModal.moneda === 'USD' ? rates.USD : rates.EUR))} MXN
                  <span style={{ color: 'var(--text-light)', marginLeft: '0.5rem' }}>
                    (1 {extraModal.moneda} = {extraModal.moneda === 'USD' ? rates.USD : rates.EUR} MXN)
                  </span>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setExtraModal(null)}
                style={{ flex: 1, padding: '0.7rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                {t('cancel')}
              </button>
              <button onClick={handlePagoExtra} disabled={!extraModal.categoria || !extraModal.monto || extraModal.saving}
                style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: '0.5rem', background: '#3b82f6', color: 'white', cursor: 'pointer', fontWeight: 600, opacity: (!extraModal.categoria || !extraModal.monto || extraModal.saving) ? 0.6 : 1 }}>
                {extraModal.saving ? t('loading') : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
